
import React from "react";
import { Topic } from "@/types/learning-path";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookText, BrainCircuit } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface SubcategoryItemProps {
  subcategory: Topic;
  status: "completed" | "in-progress" | "locked";
  recommendedDifficulty?: 'easy' | 'medium' | 'hard';
  onGenerateLecture?: (topic: string) => void;
  onGenerateInsights?: (topic: string) => void;
}

export const SubcategoryItem: React.FC<SubcategoryItemProps> = ({
  subcategory,
  status,
  recommendedDifficulty,
  onGenerateLecture,
  onGenerateInsights
}) => {
  const { isMobile } = useIsMobile();
  
  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'hard': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    }
  };

  return (
    <div className="relative pl-4 border-l border-gray-200 dark:border-gray-700 w-full">
      <div className={`p-2 sm:p-3 rounded-lg ${
        status === "completed" 
          ? "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800" 
          : status === "in-progress"
          ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
          : "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700"
      } border`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h4 className={`text-sm font-semibold ${
              status === "locked" ? "text-gray-500" : "text-[#D946EF] dark:text-purple-400"
            }`}>
              {subcategory.title}
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
              {subcategory.description}
            </p>
          </div>
          {status !== "locked" && recommendedDifficulty && (
            <Badge variant="outline" className={`text-[10px] px-1.5 ${getDifficultyColor(recommendedDifficulty)}`}>
              {recommendedDifficulty}
            </Badge>
          )}
        </div>

        {status !== "locked" && (
          <div className={`mt-2 ${isMobile ? 'flex gap-2' : 'flex gap-2'} w-full`}>
            <Button
              size="sm"
              variant="outline"
              className={`text-xs flex items-center gap-1 hover:bg-purple-50 dark:hover:bg-purple-900/20 ${isMobile ? 'flex-1 justify-center py-1' : ''}`}
              onClick={() => onGenerateLecture?.(subcategory.title)}
            >
              <BookText className="w-3.5 h-3.5" />
              {isMobile ? "Lecture" : "Generate Lecture"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className={`text-xs flex items-center gap-1 hover:bg-blue-50 dark:hover:bg-blue-900/20 ${isMobile ? 'flex-1 justify-center py-1' : ''}`}
              onClick={() => onGenerateInsights?.(subcategory.title)}
            >
              <BrainCircuit className="w-3.5 h-3.5" />
              {isMobile ? "Insights" : "AI Insights"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
